(() => {
  'use strict';

  const parsePayload = (node) => {
    if (!node) return null;
    try {
      let value = JSON.parse(node.textContent || 'null');
      if (typeof value === 'string') value = JSON.parse(value);
      return value && typeof value === 'object' ? value : null;
    } catch (_) {
      return null;
    }
  };

  const make = (tag, className = '', text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  };

  const ratingPercent = (rating) => `${Math.max(0, Math.min(100, Number(rating) / 5 * 100))}%`;

  const renderDistribution = (container, payload) => {
    if (!container) return;
    const total = Number(payload?.total_reviews || 0);
    container.textContent = '';
    for (let star = 5; star >= 1; star -= 1) {
      const count = Number(payload?.[`total_star${star}`] || 0);
      const row = make('div', 'cotton-product-reviews__distribution-row');
      row.append(make('span', '', `${star} yıldız`));
      const track = make('span', 'cotton-product-reviews__distribution-track');
      const fill = make('span', 'cotton-product-reviews__distribution-fill');
      fill.style.width = `${total ? count / total * 100 : 0}%`;
      track.append(fill);
      row.append(track, make('span', '', count));
      container.append(row);
    }
  };

  const formatDate = (raw) => {
    const source = String(raw || '').trim();
    if (!source) return '';
    const date = new Date(source.replace(' ', 'T') + 'Z');
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('tr-TR', { year: 'numeric', month: 'long' }).format(date);
  };

  const safeMediaUrl = (raw) => {
    try {
      const url = new URL(String(raw || ''), window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_) {
      return '';
    }
  };

  const renderReview = (review) => {
    const card = make('article', 'cotton-product-review-card');
    const top = make('div', 'cotton-product-review-card__top');
    const stars = make('span', 'cotton-product-review-card__stars', '★★★★★');
    stars.style.setProperty('--rating-percent', ratingPercent(review.star || 0));
    stars.setAttribute('aria-label', `${Number(review.star || 0)} yıldız`);
    top.append(stars);
    const date = formatDate(review.commented_at);
    if (date) top.append(make('time', '', date));

    const author = make('div', 'cotton-product-review-card__author');
    author.append(make('strong', '', String(review.author || 'Cotton müşterisi').trim()));
    if (Number(review.verified_badge) === 1) author.append(make('span', '', 'Doğrulanmış satın alma'));
    card.append(top, author);

    if (review.item_type) card.append(make('p', 'cotton-product-review-card__variant', `Ölçü: ${review.item_type}`));
    if (review.title) card.append(make('h3', '', review.title));
    if (review.content) card.append(make('p', 'cotton-product-review-card__content', review.content));

    const resources = Array.isArray(review.resources) ? review.resources : [];
    const media = resources.map((item) => {
      const raw = typeof item === 'string' ? item : item?.url || item?.src || item?.resource_url || item?.thumb_url;
      return safeMediaUrl(raw);
    }).filter(Boolean).slice(0, 4);
    if (media.length) {
      const gallery = make('div', 'cotton-product-review-card__media');
      media.forEach((url) => {
        const image = make('img');
        image.src = url;
        image.alt = 'Müşteri tarafından paylaşılan ürün fotoğrafı';
        image.loading = 'lazy';
        gallery.append(image);
      });
      card.append(gallery);
    }
    return card;
  };

  const init = (section) => {
    if (section.dataset.cottonReviewsReady === 'true') return;
    section.dataset.cottonReviewsReady = 'true';
    const payload = parsePayload(section.querySelector('[data-cotton-review-summary]'));
    const count = Number(payload?.total_reviews || 0);
    const rating = Number(payload?.rating || 0);
    if (!count || !rating) {
      section.hidden = true;
      return;
    }
    renderDistribution(section.querySelector('[data-cotton-review-distribution]'), payload);

    let loaded = false;
    const load = async () => {
      if (loaded) return;
      loaded = true;
      const status = section.querySelector('[data-cotton-review-status]');
      const list = section.querySelector('[data-cotton-review-list]');
      const nav = section.querySelector('[data-cotton-review-nav]');
      const prev = section.querySelector('[data-cotton-review-prev]');
      const next = section.querySelector('[data-cotton-review-next]');
      const pageLabel = section.querySelector('[data-cotton-review-page]');
      try {
        const reviews = [];
        const pageSize = 20;
        const maxPages = Math.max(1, Math.min(10, Math.ceil(count / pageSize)));
        for (let page = 1; page <= maxPages; page += 1) {
          const url = new URL('https://api.trustoo.io/api/v1/reviews/get_product_reviews');
          url.searchParams.set('shop_id', section.dataset.shopId || '');
          url.searchParams.set('limit', String(pageSize));
          url.searchParams.set('page', String(page));
          url.searchParams.set('product_id', section.dataset.productId || '');
          url.searchParams.set('sort_by', 'comprehensive-descending');
          url.searchParams.set('t', String(Date.now()));
          const response = await fetch(url, { credentials: 'omit', mode: 'cors' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const json = await response.json();
          const batch = Array.isArray(json?.data?.list) ? json.data.list : [];
          reviews.push(...batch);
          if (batch.length < pageSize || reviews.length >= count) break;
        }
        if (!reviews.length) throw new Error('empty reviews');

        list.textContent = '';
        reviews.slice(0, count).forEach((review) => list.append(renderReview(review)));
        list.hidden = false;
        status.hidden = true;
        const cards = [...list.children];
        const currentIndex = () => cards.reduce((best, card, index) => {
          const distance = Math.abs(card.offsetLeft - list.scrollLeft);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
        const updateNav = () => {
          const index = currentIndex();
          pageLabel.textContent = `${index + 1} / ${cards.length}`;
          prev.disabled = index === 0;
          next.disabled = index >= cards.length - 1;
        };
        const go = (direction) => {
          const target = Math.max(0, Math.min(cards.length - 1, currentIndex() + direction));
          list.scrollTo({ left: cards[target].offsetLeft, behavior: 'smooth' });
        };
        prev?.addEventListener('click', () => go(-1));
        next?.addEventListener('click', () => go(1));
        list.addEventListener('scroll', () => window.requestAnimationFrame(updateNav), { passive: true });
        if (nav && cards.length > 1 && window.matchMedia('(max-width: 699px)').matches) nav.hidden = false;
        updateNav();
      } catch (_) {
        status.textContent = 'Yorumlar şu anda yüklenemedi. Lütfen daha sonra tekrar deneyin.';
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '700px 0px' });
      observer.observe(section);
    } else {
      load();
    }
  };

  document.querySelectorAll('[data-cotton-reviews]').forEach(init);
})();
